import React, { useState, useEffect, useCallback } from 'react';
import { Platform, View, Text, TextInput, StyleSheet, Image, Alert, TouchableOpacity, ScrollView, SafeAreaView } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { useNavigation, useRoute, NavigationProp } from '@react-navigation/native';
import { fetchAuthSession } from 'aws-amplify/auth';
import { RootStackParamList } from './App.web';
import { apiUrl } from './utils/config';

const UpdateStoryPage: React.FC = () => {
  const route = useRoute();
  const { story } = route.params as { story: any };

  const [title, setTitle] = useState(story.title);
  const [description, setDescription] = useState(story.description);
  const [imageKey, setImageUrl] = useState(story.image_key);
  const [firstPrompt, setFirstPrompt] = useState(story.first_prompt);
  const [objective, setObjective] = useState(story.objective || '');
  const [chapters, setChapters] = useState(story.chapters.toString());
  const [category, setCategory] = useState(story.category);
  const [tags, setTags] = useState<string[]>(story.tags || []);
  const [items, setItems] = useState<string[]>(
    Array.isArray(story.items)
      ? story.items.map((item: any) => (typeof item === 'object' && item.name ? item.name : item))
      : []
  );  
  const [newItem, setNewItem] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [outline, setOutline] = useState(story.outline || []);

  const navigation = useNavigation<NavigationProp<RootStackParamList>>();

  const getAuthToken = useCallback(async (): Promise<any | null> => {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.idToken || null;
    } catch (error) {
      console.error('Error retrieving auth token:', error);
      return null;
    }
  }, []);

  const handleSubmit = async () => {
    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to retrieve auth token');
  
      const response = await fetch(`${apiUrl}stories/update-story`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          story_id: story.story_id,
          title,
          chapters,
          description,
          image_key: imageKey,
          first_prompt: firstPrompt,
          objective,
          category,
          tags,
          items,
          outline // Include outline in the request
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      navigation.navigate('StoriesPage');
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Error updating story:', error);
        setError(error.message);
      }
    }
  };
  

  const handleTagChange = (tag: string) => {
    setTags((prevTags) => prevTags.includes(tag) ? prevTags.filter(t => t !== tag) : [...prevTags, tag]);
  };

  const handleAddItem = () => {
    if (newItem.trim()) {
      setItems([...items, newItem.trim()]);
      setNewItem('');
    }
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('StoriesPage'); // Navigate to StoriesPage explicitly
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => handleBack()}>
            <Image source={require('./assets/back-btn.png')} style={styles.backIcon} />
          </TouchableOpacity>
        </View>
        <View style={styles.titleContainer}>
          <Text style={styles.heading}>Update Story</Text>
          {error && <Text style={styles.error}>{error}</Text>}
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Title:</Text>
          <TextInput style={styles.input} value={title} onChangeText={setTitle} />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Chapters:</Text>
          <TextInput style={styles.input} keyboardType="numeric" value={chapters} onChangeText={setChapters} />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Description:</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={description}
            onChangeText={setDescription}
            multiline
          />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Image Key:</Text>
          <TextInput style={styles.input} value={imageKey} onChangeText={setImageUrl} />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>First Prompt:</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={firstPrompt}
            onChangeText={setFirstPrompt}
            multiline
          />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Objective:</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={objective}
            onChangeText={setObjective}
            multiline
          />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Category:</Text>
          <Picker selectedValue={category} onValueChange={setCategory} style={styles.input}>
            <Picker.Item label="Mythical" value="Mythical" />
            <Picker.Item label="Medieval" value="Medieval" />
            <Picker.Item label="Whimsical" value="Whimsical" />
            <Picker.Item label="Adventure" value="Adventure" />
          </Picker>
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Tags:</Text>
          {['Popular', 'New', 'Seasonal'].map(tag => (
            <TouchableOpacity key={tag} style={styles.tagButton} onPress={() => handleTagChange(tag)}>
              <Text style={[styles.tagText, tags.includes(tag) && styles.selectedTag]}>{tag}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Outline (JSON format):</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={JSON.stringify(outline, null, 2)} // Pretty-print JSON
            onChangeText={(text) => {
              try {
                setOutline(JSON.parse(text)); // Parse JSON input safely
              } catch (error) {
                console.error("Invalid JSON input", error);
              }
            }}
            multiline
          />
        </View>
        <View style={styles.formGroup}>
          <Text style={styles.label}>Add Item:</Text>
          <TextInput style={styles.input} value={newItem} onChangeText={setNewItem} />
          <TouchableOpacity style={styles.addButton} onPress={handleAddItem}>
            <Text style={styles.addButtonText}>Add</Text>
          </TouchableOpacity>
        </View>
        {items.map((item, index) => (
          <View key={index} style={styles.itemRow}>
            <Text style={styles.item}>{item}</Text>
            <TouchableOpacity onPress={() => handleRemoveItem(index)}>
              <Text style={styles.removeItem}>✖</Text>
            </TouchableOpacity>
          </View>
        ))}
        <TouchableOpacity style={styles.submitButton} onPress={handleSubmit}>
          <Text style={styles.submitButtonText}>Update Story</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  scrollContainer: {
    paddingBottom: 20,
  },
  backIcon: {
    width: 24,
    height: 24,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    ...Platform.select({
      web: {
        padding: 15,
      },
    }),
  },
  titleContainer: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 800,
  },
  heading: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'left',
  },
  formGroup: {
    marginBottom: 15,
    maxWidth: 800,
    width: '100%',
    alignSelf: 'center',
  },
  label: {
    fontWeight: 'bold',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 10,
    borderRadius: 5,
    backgroundColor: '#fff',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  tagButton: {
    padding: 10,
    marginVertical: 5,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#ccc',
  },
  tagText: {
    fontSize: 16,
  },
  selectedTag: {
    color: 'blue',
    fontWeight: 'bold',
  },
  addButton: {
    backgroundColor: '#4CAF50',
    padding: 10,
    marginTop: 10,
    borderRadius: 5,
    alignItems: 'center',
  },
  addButtonText: {
    color: 'white',
    fontWeight: 'bold',
  },
  submitButton: {
    backgroundColor: '#4CAF50',
    padding: 15,
    borderRadius: 5,
    alignSelf: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: 800,
    marginVertical: 20,
  },
  submitButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
  },
  error: {
    color: 'red',
    marginBottom: 10,
  },
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
  },
  item: {
    fontSize: 16,
    marginBottom: 5,
  },
  removeItem: {
    color: 'red',
    fontSize: 18,
    fontWeight: 'bold',
  },
})

export default UpdateStoryPage;
